import { Tooltip, TooltipContent, TooltipTrigger, cn } from '@solinkcorp/solink-ui';
import { type NewActivityType } from 'components/ScheduledActivityForm/useScheduledEventForm';
import { Info, Loader2 } from 'lucide-react';
import React from 'react';
import useLocale from 'src/hooks/useLocale';
import { useSelector } from 'stores/store';
import LocaleUtil from 'utils/LocaleUtil';
import { MAX_NUMBER_OF_SELECTED_CAMERAS } from 'vd-constants';
import { useScheduledSpotCheckMonthlyUsage } from '../hooks/useScheduledSpotCheckMonthlyUsage';

interface SpotCheckFooterProps {
  newActivity: NewActivityType;
  totalEventsInLast30Days: number | undefined;
  hasSelectedEventReport: boolean;
  isEventsEstimateBelowLimit: boolean;
  availableQuota: number | undefined;
  displayLoadingQuota: boolean;
  estimatedTotalIsolatedRemainingQuota: number | undefined;
  configuredTotalQuota: number | undefined;
}

function FooterHint({ text }: { text: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="tw inline-flex shrink-0 text-foreground-muted hover:text-foreground-secondary"
          aria-label={text}
        >
          <Info className="size-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="tw max-w-sm text-left">
        {text}
      </TooltipContent>
    </Tooltip>
  );
}

export default function SpotCheckFooter(props: SpotCheckFooterProps) {
  const {
    newActivity,
    totalEventsInLast30Days,
    hasSelectedEventReport,
    isEventsEstimateBelowLimit,
    availableQuota,
    displayLoadingQuota,
    estimatedTotalIsolatedRemainingQuota,
    configuredTotalQuota,
  } = props;
  const locale = useLocale();

  const partialSpotCheck = React.useMemo(
    () => ({
      startsAt: newActivity.startsAt,
      endsAt: newActivity.endsAt,
      reoccurringCount: newActivity.reoccurringCount,
      rule: newActivity.rule,
      cameraIds: newActivity.cameraIds,
    }),
    [
      newActivity.startsAt,
      newActivity.endsAt,
      newActivity.reoccurringCount,
      newActivity.rule,
      newActivity.cameraIds,
    ],
  );

  const spotCheckMode = useSelector((state) => state.filter.spotCheckMode);
  const isLivePreviewMode = spotCheckMode === 'live-preview';
  const isEventSpotCheck = spotCheckMode === 'event';
  const isScheduledSpotCheck = spotCheckMode === 'scheduled';

  const { monthlyUsage: scheduledSpotCheckMonthlyUsage } = useScheduledSpotCheckMonthlyUsage(
    isScheduledSpotCheck ? partialSpotCheck : undefined,
  );

  const spotCheckMonthlyUsage = isScheduledSpotCheck
    ? scheduledSpotCheckMonthlyUsage
    : totalEventsInLast30Days;

  const isMonthlyOverRemaining =
    spotCheckMonthlyUsage != null &&
    availableQuota != null &&
    spotCheckMonthlyUsage > availableQuota;

  const numberOfSelectedCameras = newActivity.cameraIds.length;

  const spotCheckLivePreviewNumberOfCamerasText = LocaleUtil.getLocalizedStr({
    string: locale.scheduledAIChat.spotCheckLivePreviewNumberOfCameras,
    replacements: {
      current: numberOfSelectedCameras,
      maximumNumber: MAX_NUMBER_OF_SELECTED_CAMERAS,
    },
  });

  const spotCheckLivePreviewNumberOfCamerasInfo = LocaleUtil.getLocalizedStr({
    string: locale.scheduledAIChat.spotCheckLivePreviewNumberOfCamerasInfo,
    replacements: { maximumNumber: MAX_NUMBER_OF_SELECTED_CAMERAS },
  });

  const spotCheckRemainingBalanceText = LocaleUtil.getLocalizedStr({
    string:
      estimatedTotalIsolatedRemainingQuota !== undefined
        ? locale.scheduledAIChat.spotCheckRemainingIsolatedBalance
        : locale.scheduledAIChat.spotCheckRemainingBalance,
    replacements: { remaining: availableQuota },
  });

  const scheduledSpotCheckEstimatedUsageText = LocaleUtil.getLocalizedStr({
    string: locale.scheduledAIChat.spotCheckEstimatedUsage,
    replacements: { usage: spotCheckMonthlyUsage },
  });

  const eventSpotCheckEstimatedUsageText = LocaleUtil.getLocalizedStr({
    string: locale.scheduledAIChat.spotCheckEstimatedUsage,
    replacements: { usage: totalEventsInLast30Days },
  });

  const isNegativeAvailableQuota = availableQuota != null && availableQuota < 0;
  const displayEventEstimatedUsage =
    isEventSpotCheck &&
    !isLivePreviewMode &&
    hasSelectedEventReport &&
    totalEventsInLast30Days != null;
  const displayScheduledEstimatedUsage =
    isScheduledSpotCheck && !isLivePreviewMode && spotCheckMonthlyUsage != null;
  const displaySpotCheckUsage = displayEventEstimatedUsage || displayScheduledEstimatedUsage;
  const displayAvailableQuota =
    (isEventSpotCheck || isScheduledSpotCheck) && availableQuota != null;

  const spotCheckQuotaInfoText = isEventSpotCheck
    ? locale.scheduledAIChat.eventSpotCheckBalanceInfo
    : locale.scheduledAIChat.scheduledSpotCheckBalanceInfo;

  const isolatedUsageInfoText = LocaleUtil.getLocalizedStr({
    string: locale.scheduledAIChat.spotCheckIsolatedBalanceInfo,
    replacements: { quota: configuredTotalQuota },
  });

  const usageInfoText =
    (estimatedTotalIsolatedRemainingQuota !== undefined
      ? isolatedUsageInfoText
      : spotCheckQuotaInfoText) +
    ' ' +
    (isNegativeAvailableQuota
      ? locale.scheduledAIChat.clearUpSpotChecks
      : locale.scheduledAIChat.spotCheckBalanceResetInfo);

  const spotCheckUsageInfoText = isEventSpotCheck
    ? locale.scheduledAIChat.eventSpotCheckUsageInfo
    : locale.scheduledAIChat.scheduledSpotCheckUsageInfo;

  const showEstimatedUsageError = !isEventsEstimateBelowLimit || isMonthlyOverRemaining;

  if (displayLoadingQuota) {
    return (
      <div className="tw flex items-center gap-2 text-foreground-secondary body-sm">
        <Loader2 className="size-4 animate-spin" />
      </div>
    );
  }

  return (
    <div className="tw flex flex-wrap items-center gap-x-3 gap-y-1 text-foreground-secondary body-sm">
      {isLivePreviewMode && !!numberOfSelectedCameras ? (
        <span className="tw inline-flex items-center gap-1">
          {spotCheckLivePreviewNumberOfCamerasText}
          <FooterHint text={spotCheckLivePreviewNumberOfCamerasInfo} />
        </span>
      ) : null}
      {displayAvailableQuota ? (
        <span
          className={cn(
            'tw inline-flex items-center gap-1',
            isNegativeAvailableQuota && 'text-status-red',
          )}
        >
          {spotCheckRemainingBalanceText}
          <FooterHint text={usageInfoText} />
        </span>
      ) : null}
      {displaySpotCheckUsage && displayAvailableQuota ? (
        <span className="tw mx-0.5 h-4 w-px shrink-0 self-center bg-border-muted" aria-hidden />
      ) : null}
      {displaySpotCheckUsage ? (
        <span
          className={cn(
            'tw inline-flex items-center gap-1',
            showEstimatedUsageError && 'text-status-red',
          )}
          data-testid={showEstimatedUsageError ? 'spot-check-estimated-usage-error' : undefined}
        >
          {isEventSpotCheck
            ? eventSpotCheckEstimatedUsageText
            : scheduledSpotCheckEstimatedUsageText}
          <FooterHint
            text={
              isMonthlyOverRemaining
                ? locale.scheduledAIChat.insufficientSpotCheckBalance
                : spotCheckUsageInfoText
            }
          />
        </span>
      ) : null}
    </div>
  );
}
